import React, { useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Container, VStack, Spinner, Center, Alert, AlertIcon, Button } from '@chakra-ui/react';
import { useCourseData } from '../hooks/useCourseData';
import trackingService from '../services/trackingService';

// Section Components
import CourseHero from '../components/course/CourseHero';
import CourseAudienceCheck from '../components/course/CourseAudienceCheck';
import CourseOutcomes from '../components/course/CourseOutcomes';
import CourseFeatures from '../components/course/CourseFeatures';
import CourseCurriculum from '../components/course/CourseCurriculum';
import CourseCurriculumBreakdown from '../components/course/CourseCurriculumBreakdown';
import CourseTestimonials from '../components/course/CourseTestimonials';
import CourseFAQ from '../components/course/CourseFAQ';
import CoursePricing from '../components/course/CoursePricing';
import TrustBadges from '../components/course/TrustBadges';

/**
 * Course Landing Page
 * Dynamic, data-driven landing page for any course
 *
 * Route: /course/:courseId
 * Data source: /marketing_meta/courses/{courseId} + /testmeta/hierarchy/
 */
const CourseLanding = () => {
  const { courseId: rawCourseId } = useParams();
  const navigate = useNavigate();

  // Parse courseId parameter in format: "11-abc-flg" (courseId-influencerId-flags)
  const parts = rawCourseId?.split('-') || [];
  const courseId = parts[0] || '1';
  const influencerId = parts[1] || '';
  const flags = parts[2] || '';

  // Set influencer if provided
  React.useEffect(() => {
    if (influencerId) {
      trackingService.setInfluencer(influencerId);
      trackingService.track('course_landing_visit', {
        courseId,
        influencerId,
        flags
      });
    }
  }, [influencerId, courseId, flags]);

  const { courseData, loading, error, isValid } = useCourseData(courseId);

  // Handle enroll button click - scroll to pricing
  const handleEnrollClick = () => {
    const pricingSection = document.querySelector('[data-pricing]');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Loading State
  if (loading) {
    return (
      <Center minH="100vh" bg="white">
        <VStack spacing={4}>
          <Spinner size="xl" color="brand.600" />
          <Box fontSize="lg" color="brand.600">
            Loading course...
          </Box>
        </VStack>
      </Center>
    );
  }

  // Error State
  if (error || !isValid) {
    return (
      <Box minH="100vh" bg="white" py={20}>
        <Container maxW="container.md">
          <Alert
            status="error"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="auto"
            py={12}
            borderRadius="lg"
          >
            <AlertIcon boxSize="40px" mr={0} mb={4} />
            <Box fontSize="lg" fontWeight="bold" mb={2}>
              Course Not Found
            </Box>
            <Box fontSize="sm" color="gray.600" mb={6}>
              {error || 'The course you are looking for is not available or is still being set up.'}
            </Box>
            <Box fontSize="xs" color="gray.500" mb={4}>
              Our admin team has been notified about this issue.
            </Box>
            <Button as="a" href="/" colorScheme="brand" size="sm">
              Return to Home
            </Button>
          </Alert>
        </Container>
      </Box>
    );
  }

  // Data Validation
  if (!courseData) {
    return (
      <Center minH="100vh" bg="white">
        <Alert status="warning">
          <AlertIcon />
          No course data available
        </Alert>
      </Center>
    );
  }

  // Extract sections (conditional rendering)
  const {
    hero,
    audienceCheck,
    outcomes,
    features,
    curriculum,
    testimonials,
    faq,
    trustBadges,
    pricing,
    settings
  } = courseData;

  return (
    <Box bg="white">
      {/* ========== HERO SECTION ========== */}
      {hero && (
        <CourseHero
          data={hero}
          startDate={settings?.courseStartDate}
          onEnrollClick={handleEnrollClick}
          courseId={courseId}
        />
      )}

      {/* ========== AUDIENCE CHECK (Who is this for?) ========== */}
      {audienceCheck && (
        <CourseAudienceCheck data={audienceCheck} />
      )}

      {/* ========== OUTCOMES (What you'll learn) ========== */}
      {outcomes && (
        <CourseOutcomes data={outcomes} />
      )}

      {/* ========== FEATURES (Why choose this course) ========== */}
      {features && (
        <CourseFeatures data={features} />
      )}

      {/* ========== CURRICULUM ========== */}
      {/* TODO: Temporarily using hardcoded curriculum breakdown. Restore DB-driven component when ready:
      {curriculum?.timeline?.length > 0 && (
        <CourseCurriculum
          data={curriculum}
          onEnrollClick={handleEnrollClick}
        />
      )}
      */}
      <CourseCurriculumBreakdown />

      {/* ========== TESTIMONIALS (Social proof) ========== */}
      {testimonials && (
        <CourseTestimonials data={testimonials} />
      )}

      {/* ========== FAQ ========== */}
      {faq && (
        <CourseFAQ data={faq} />
      )}

      {/* ========== PRICING ========== */}
      {pricing && (
        <CoursePricing
          data={pricing}
          startDate={settings?.courseStartDate}
          courseId={courseId}
        />
      )}

      {/* ========== TRUST BADGES ========== */}
      {trustBadges && (
        <TrustBadges data={trustBadges} />
      )}
    </Box>
  );
};

export default CourseLanding;
